import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {
  TournamentDispatchDecision,
  TournamentDispatcherControlCommand,
  TournamentDispatcherState,
  TournamentMatchDispatchCommand,
} from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";
import { useState } from "react";

type MatchOverride = {
  match: Schema["TournamentMatchRef"];
  expected_revision: number;
  allowed_actions: string[];
  label: string;
};

type Props = {
  dispatcher: TournamentDispatcherState;
  recommendations: TournamentDispatchDecision[];
  overrides: MatchOverride[];
  courts?: Array<{ court_id: string; label: string }>;
  onControlCommand: (
    command: TournamentDispatcherControlCommand,
  ) => Promise<unknown>;
  onMatchOverride: (
    match: Schema["TournamentMatchRef"],
    command: TournamentMatchDispatchCommand,
  ) => Promise<unknown>;
  /** Reload authoritative revisions after a 409; never replay a stale mutation. */
  onRefresh?: () => Promise<unknown>;
};

const commandId = () => crypto.randomUUID();
const label = (value: string) => value.replaceAll("_", " ");
const matchKey = (match: Schema["TournamentMatchRef"]) =>
  `${match.kind}:${match.match_id}`;
const conflict = (reason: unknown) =>
  (reason as { status?: number } | undefined)?.status === 409 ||
  /revision|conflict|stale/i.test(
    reason instanceof Error ? reason.message : "",
  );

export function TournamentDispatchPanel({
  dispatcher,
  recommendations,
  overrides,
  courts = [],
  onControlCommand,
  onMatchOverride,
  onRefresh,
}: Props) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState<(() => Promise<void>) | null>(null);
  const [retryLabel, setRetryLabel] = useState("Retry command");
  const [delayAt, setDelayAt] = useState<Record<string, string>>({});
  const [courtByMatch, setCourtByMatch] = useState<Record<string, string>>({});
  const matchOverrides = overrides;

  const resolveFailure = async (
    reason: unknown,
    retryLabel: string,
    retryAction: () => Promise<void>,
  ) => {
    const refresh = onRefresh;
    if (conflict(reason) && refresh) {
      try {
        await refresh();
        setError(
          "Tournament state changed. Review the refreshed schedule before trying again.",
        );
        setRetry(null);
      } catch {
        setError(
          "Tournament state changed and could not be refreshed. Retry refresh before another command.",
        );
        setRetry(() => async () => {
          setPending(true);
          try {
            await refresh();
            setError(null);
            setRetry(null);
          } catch (refreshReason) {
            setError(
              refreshReason instanceof Error
                ? refreshReason.message
                : "Tournament refresh failed.",
            );
          } finally {
            setPending(false);
          }
        });
        setRetryLabel("Retry refresh");
      }
      return;
    }
    setError(reason instanceof Error ? reason.message : retryLabel);
    setRetry(() => retryAction);
    setRetryLabel(retryLabel);
  };

  const changeMode = async (
    mode: "manual" | "auto_court_assignment" | "automatic",
  ) => {
    setPending(true);
    setError(null);
    try {
      await onControlCommand({
        command: "set_mode",
        idempotency_key: commandId(),
        expected_revision: dispatcher.revision,
        mode,
        ...(mode === "manual" ? { reason: "organizer_override" } : {}),
      });
      setRetry(null);
    } catch (reason) {
      await resolveFailure(reason, "Retry mode change", () => changeMode(mode));
    } finally {
      setPending(false);
    }
  };

  const automaticOptIn = async (
    command: "grant_automatic_opt_in" | "revoke_automatic_opt_in",
  ) => {
    setPending(true);
    setError(null);
    try {
      await onControlCommand({
        command,
        idempotency_key: commandId(),
        expected_revision: dispatcher.revision,
        ...(command === "revoke_automatic_opt_in"
          ? { reason: "organizer_override" }
          : {}),
      });
      setRetry(null);
    } catch (reason) {
      await resolveFailure(reason, "Retry automatic setting", () =>
        automaticOptIn(command),
      );
    } finally {
      setPending(false);
    }
  };

  const applyOverride = async (
    target: MatchOverride,
    command: TournamentMatchDispatchCommand,
  ) => {
    setPending(true);
    setError(null);
    try {
      await onMatchOverride(target.match, command);
      setRetry(null);
    } catch (reason) {
      await resolveFailure(reason, "Retry match override", () =>
        applyOverride(target, command),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Paper
      component="section"
      aria-label="Tournament dispatch"
      variant="outlined"
      sx={{ p: 2 }}
    >
      <Stack spacing={1.5}>
        <Typography component="h2" variant="h6">
          Dispatch
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {dispatcher.mode === "manual"
            ? "Manual mode — recommendations only"
            : label(dispatcher.mode)}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {dispatcher.lease_active
            ? "Dispatcher lease active"
            : "Dispatcher lease will recover automatically"}
        </Typography>
        {error ? (
          <Alert
            role="alert"
            severity="error"
            action={
              retry ? (
                <Button size="small" onClick={() => void retry()}>
                  {retryLabel}
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button disabled={pending} onClick={() => void changeMode("manual")}>
            Manual
          </Button>
          <Button
            disabled={pending}
            onClick={() => void changeMode("auto_court_assignment")}
          >
            Automatic court assignment
          </Button>
          {!dispatcher.automatic_opt_in ? (
            <Button
              disabled={pending}
              onClick={() => void automaticOptIn("grant_automatic_opt_in")}
            >
              Enable automatic calls
            </Button>
          ) : (
            <Button
              disabled={pending}
              onClick={() => void automaticOptIn("revoke_automatic_opt_in")}
            >
              Disable automatic calls
            </Button>
          )}
          <Button
            disabled={pending || !dispatcher.automatic_opt_in}
            onClick={() => void changeMode("automatic")}
          >
            Automatic dispatch
          </Button>
        </Stack>
        {recommendations.map((recommendation) => {
          const display =
            matchOverrides.find(
              (item) =>
                item.match.kind === recommendation.match.kind &&
                item.match.match_id === recommendation.match.match_id,
            )?.label ?? "Scheduled match";
          return (
            <Stack key={recommendation.decision_id} direction="row" spacing={1}>
              <Typography>{`${display} · ${label(recommendation.action)}`}</Typography>
              {recommendation.court_allocation_id ? (
                <Typography color="text.secondary" variant="body2">
                  Court recommendation available
                </Typography>
              ) : null}
            </Stack>
          );
        })}
        {matchOverrides.map((target) => {
          const key = matchKey(target.match);
          const selectedCourt = courtByMatch[key] ?? courts[0]?.court_id ?? "";
          const delayValue = delayAt[key] ?? "";
          const display = target.label;
          return (
            <Stack
              key={key}
              component="section"
              spacing={1}
              sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}
            >
              <Typography variant="subtitle2">{display}</Typography>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                {target.allowed_actions.includes("hold") ? (
                  <Button
                    disabled={pending}
                    onClick={() =>
                      void applyOverride(target, {
                        command: "hold",
                        idempotency_key: commandId(),
                        expected_revision: target.expected_revision,
                        reason: "organizer_override",
                      })
                    }
                  >
                    Hold match: {display}
                  </Button>
                ) : null}
                {target.allowed_actions.includes("release") ? (
                  <Button
                    disabled={pending}
                    onClick={() =>
                      void applyOverride(target, {
                        command: "release",
                        idempotency_key: commandId(),
                        expected_revision: target.expected_revision,
                      })
                    }
                  >
                    Release match: {display}
                  </Button>
                ) : null}
              </Stack>
              {target.allowed_actions.includes("delay") ? (
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: "wrap" }}
                >
                  <TextField
                    label={`Delay ${display} until`}
                    size="small"
                    type="datetime-local"
                    value={delayValue}
                    onChange={(event) =>
                      setDelayAt((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }))
                    }
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <Button
                    disabled={pending || !delayValue}
                    onClick={() =>
                      void applyOverride(target, {
                        command: "delay",
                        idempotency_key: commandId(),
                        expected_revision: target.expected_revision,
                        new_not_before: new Date(delayValue).toISOString(),
                        reason: "schedule_delay",
                      })
                    }
                  >
                    Delay match: {display}
                  </Button>
                </Stack>
              ) : null}
              {target.allowed_actions.includes("re_court") ? (
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{ flexWrap: "wrap" }}
                >
                  <TextField
                    label={`Court for ${display}`}
                    select
                    size="small"
                    value={selectedCourt}
                    onChange={(event) =>
                      setCourtByMatch((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }))
                    }
                    sx={{ minWidth: 180 }}
                  >
                    {courts.map((court) => (
                      <MenuItem key={court.court_id} value={court.court_id}>
                        {court.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    disabled={pending || !selectedCourt}
                    onClick={() =>
                      void applyOverride(target, {
                        command: "re_court",
                        idempotency_key: commandId(),
                        expected_revision: target.expected_revision,
                        new_court_id: selectedCourt,
                      })
                    }
                  >
                    Re-court match: {display}
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}
